import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Form, Input, Checkbox, Spin } from "antd";
import { Loader2 } from "lucide-react";

import { postMethod } from "../services/HttpServices";
import { signInUrl } from "../constant/Constant";
import { getAccountInfo, logout } from "../actionCreator/commonActionCreator";
import { getTimeoutMessage } from "../utils/utils";
import "../assets/css/auth.css";
import SocialAuth from "../components/social-auth/SocialAuth";
import AuthLeftContainer from "../common/AuthLeftContainer";
import { AuthContext } from "../context/AuthContext";
import { useAlert } from "../context/AlertProvider";
import moment from "moment";

const LoginNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { login, isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [sessionExpireMsg, setSessionExpireMsg] = useState("");
  const [token, setToken] = useState();

  useEffect(() => {
    setSessionExpireMsg(getTimeoutMessage());
    localStorage.setItem("sessionTimeout", "");

    if (isAuthenticated) {
      navigate(-1);
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await postMethod(signInUrl, values);
      const res = await dispatch(getAccountInfo(response.data.id_token));
      login(
        response.data.id_token,
        values?.rememberMe,
        res,
        response?.data?.refresh_token,
      );

      if (res?.passwordChangeNeeded) {
        navigate("/account/change-password");
      } else {
        const inputDateTime = moment(res?.validTo); // Replace with your date
        const now = moment();
        if (inputDateTime.isBefore(now)) {
          navigate("/account/subscription-expired");
        } else {
          navigate("/dashboard-view");
        }
      }
      setLoading(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        setError(error?.response?.data?.detail);
      } else if (error?.response?.status === 409) {
        setToken(error?.response?.data?.id_token);
      } else {
        setError(
          error?.response?.data?.title === "Email settings not found" ? (
            <>
              <span>It appears you need to create an account first.</span>
              <span>
                👉 Please{" "}
                <Link to="/signup" className="text-theme">
                  sign up
                </Link>{" "}
                before attempting to log in.
              </span>
            </>
          ) : (
            error?.response?.data?.title
          ),
        );
      }
      setLoading(false);
    }
  };

  const handleSession = async () => {
    const res = await dispatch(logout(token));
    if (res?.status === 200) {
      showAlert(
        "success",
        "Your session has ended on another device. Please log in again to continue here.",
      );
      setToken();
    } else {
      showAlert("error", "Something went wrong. Please try again.");
    }
  };

  return (
    <Container
      fluid
      className="min-vh-100 h-100 p-0 text-black position-relative roboto-regular auth overflow-x-hidden"
    >
      <Row className="min-vh-100 h-100 m-0">
        <Col lg={4} className="d-none p-0 d-lg-block">
          <AuthLeftContainer />
        </Col>
        <Col xs={12} lg={8} className="p-5">
          <div className="text-right heading12px fw-semibold">
            Don’t have an account?{" "}
            <NavLink
              to="/signup"
              className="heading12px text-theme fw-semibold"
            >
              SignUp
            </NavLink>
          </div>
          <div className="d-flex flex-column auth-card justify-content-center h-100">
            <div className="login-card">
              <div className="heading20px fw-semibold pb-4">Sign In</div>
              <SocialAuth isSignin />
              <div className="heading8px text-gray py-4 text-center seprater">
                Or Sign in with
              </div>
              <Form
                onFinish={handleLogin}
                initialValues={{
                  username: "",
                  password: "",
                  rememberMe: false,
                }}
              >
                {sessionExpireMsg && (
                  <p className="text-error text-center pb-2">
                    {sessionExpireMsg}
                  </p>
                )}

                {error && (
                  <p className="text-error text-center pb-2">{error}</p>
                )}
                <Form.Item
                  name="username"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter valid E-mail!",
                    },
                    { required: true, message: "Please enter your email." },
                  ]}
                >
                  <div className="d-flex flex-column">
                    <label className="heading12px text-gray pb-1">
                      Email<span className="text-error">*</span>
                    </label>
                    <Input
                      placeholder="Enter Your Email"
                      className="auth-input"
                    />
                  </div>
                </Form.Item>

                <Form.Item
                  name="password"
                  className="mb-0"
                  rules={[
                    { required: true, message: "Please enter password." },
                  ]}
                >
                  <div className="d-flex flex-column">
                    <label className="heading12px text-gray pb-1">
                      Password<span className="text-error">*</span>
                    </label>
                    <div className="position-relative border-none w-100">
                      <Input.Password
                        placeholder="Enter Your Password"
                        className="auth-input w-100"
                        onKeyDown={(e) => e.key === " " && e.preventDefault()}
                      />
                    </div>
                  </div>
                </Form.Item>

                <div className="d-flex align-items-center justify-content-between">
                  <Form.Item
                    name="rememberMe"
                    valuePropName="checked"
                    className="mb-0"
                  >
                    <Checkbox>
                      <span className="heading12px fw-semibold">
                        Remember Me?
                      </span>
                    </Checkbox>
                  </Form.Item>
                  <NavLink
                    to="/forgetpassword"
                    className="heading12px text-theme text-right fw-semibold"
                  >
                    Forgot Password?
                  </NavLink>
                </div>

                {token && (
                  <div className="mt10 heading13px fw-semibold">
                    This user has already logged in. Please try different
                    credentials or{" "}
                    <span
                      className="text-theme cursor-pointer"
                      onClick={handleSession}
                    >
                      click here
                    </span>{" "}
                    to invalidate the previous session.
                  </div>
                )}

                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ width: "100%" }}
                  className="theme-btn mt-4"
                  disabled={loading}
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <Loader2
                          className="tw-w-4 tw-h-4 tw-animate-spin tw-text-white"
                        />
                      }
                    />
                  ) : (
                    "Login"
                  )}
                </Button>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginNew;
